import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import TagsEditor from "../../TagsEditor";
import TabNavigation from "../TabNavigation";

import { Actions } from "./Actions";
import { Description } from "./Description";
import { MetaInfo } from "./MetaInfo";
import { Title } from "./Title";
import { ToggleVersions } from "./ToggleVersions";
import { ResourceHeaderProps } from "./types";

import EntityStarButton from "@/components/EntityStarButton";
import { useBreadcrumbToolbarContainer } from "@/components/UnifiedRegistry/breadcrumb-toolbar-context";
import { getItemColor } from "@/components/UnifiedRegistry/utils";
import { EntityIcon } from "@/components/ui/icons";
import { cn } from "@/lib/utils";

export const ResourceHeader = ({
  type,
  title,
  description,
  onTitleEdit,
  onDescriptionEdit,
  renderAfterTitle,
  renderRightContent,
  id,
  createdAt,
  versionInfo,
  format,
  tags,
  onTagsEdit,
  actions = [],
  rightActions = [],
  dropdownActions = [],
  showVersions,
  onToggleVersions,
  isLoading = false,
  className,
  badgeText,
  badgeVariant,
  minimal,
  titleInBreadcrumb = false,
  tabsProps,
  additionalInfo,
  actionsAlignment = "left",
}: ResourceHeaderProps) => {
  const hasMetaInfo =
    createdAt ||
    id !== -1 ||
    (versionInfo && versionInfo.total > 1) ||
    format ||
    showVersions ||
    isLoading;

  const showStar = Boolean(type && id !== -1 && id !== "");

  const [prevId, setPrevId] = useState<string | number>(id);
  const [prevTitle, setPrevTitle] = useState<string>(title);
  const shouldRenderDescription = onDescriptionEdit !== undefined || description !== undefined;
  const breadcrumbToolbarContainer = useBreadcrumbToolbarContainer();

  useEffect(() => {
    if (id !== prevId || title !== prevTitle) {
      setPrevId(id);
      setPrevTitle(title);
    }
  }, [id, title, prevId, prevTitle]);

  if (titleInBreadcrumb) {
    const toolbarControls = (
      <div className="flex min-w-0 items-center gap-1">
        {tags && onTagsEdit && (
          <div className="min-w-0 flex-shrink">
            <TagsEditor tags={tags} onEdit={onTagsEdit} maxTags={10} type={type} />
          </div>
        )}
        {showStar && type && <EntityStarButton entityType={type} entityId={id} />}
        {renderRightContent}
        <Actions
          actions={actionsAlignment === "right" ? [...actions, ...rightActions] : actions}
          dropdownActions={dropdownActions}
        />
        {actionsAlignment !== "right" && <Actions actions={rightActions} dropdownActions={[]} />}
        <ToggleVersions
          standalone
          showVersions={showVersions}
          onToggleVersions={onToggleVersions}
          versionTotal={versionInfo?.total}
        />
      </div>
    );
    const hasSupplementalInfo = shouldRenderDescription || (!minimal && hasMetaInfo);

    return (
      <div
        className={`flex min-h-0 w-full min-w-0 flex-col overflow-hidden ${tabsProps ? "h-full" : ""}`}
      >
        {breadcrumbToolbarContainer
          ? createPortal(toolbarControls, breadcrumbToolbarContainer)
          : toolbarControls}
        {hasSupplementalInfo && (
          <div className={cn("flex min-w-0 items-center gap-3 pb-2", className)}>
            {shouldRenderDescription && (
              <div className="min-w-0 max-w-xl">
                <Description
                  description={description}
                  onDescriptionEdit={onDescriptionEdit}
                  type={type}
                />
              </div>
            )}
            {!minimal && hasMetaInfo && (
              <MetaInfo
                createdAt={createdAt}
                additionalInfo={additionalInfo}
                id={id}
                versionInfo={versionInfo}
                format={format}
                showVersions={showVersions}
                onToggleVersions={onToggleVersions}
                isLoading={isLoading}
              />
            )}
          </div>
        )}
        {tabsProps && (
          <div className="min-h-0 flex-1 overflow-auto">
            <TabNavigation {...tabsProps} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`flex min-h-0 w-full min-w-0 flex-col overflow-hidden ${tabsProps ? "h-full" : ""}`}
    >
      <div className={`flex min-w-0 ${className}`}>
        <div className="min-w-0 flex-1">
          {hasMetaInfo ? (
            <div className="flex min-w-0 flex-col gap-y-2">
              <div className="flex min-w-0 items-center justify-between gap-x-3 gap-y-2">
                <div className="flex min-w-[8rem] flex-1 items-center gap-x-2">
                  {type && (
                    <div
                      className={`hidden h-10 w-10 flex-shrink-0 items-center justify-center rounded-full sm:flex ${getItemColor(
                        type,
                      )}`}
                    >
                      <EntityIcon type={type} size="md" />
                    </div>
                  )}
                  <div
                    className={`flex min-w-0 ${
                      actionsAlignment === "right" && rightActions.length > 0
                        ? "flex-1 justify-between"
                        : ""
                    } flex-row items-start gap-x-2`}
                  >
                    <div className="flex min-w-0 flex-1 items-start gap-x-2 overflow-hidden">
                      <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 items-center gap-x-2 overflow-hidden">
                          <Title
                            key={`${id}-${title}`}
                            title={title}
                            onTitleEdit={onTitleEdit}
                            badgeText={badgeText}
                            badgeVariant={badgeVariant}
                            type={type}
                          />
                          {renderAfterTitle}
                        </div>
                        {shouldRenderDescription && (
                          <Description
                            description={description}
                            onDescriptionEdit={onDescriptionEdit}
                            type={type}
                          />
                        )}
                      </div>
                      {tags && onTagsEdit && (
                        <div className="flex-shrink-0 sm:px-2">
                          <TagsEditor tags={tags} onEdit={onTagsEdit} maxTags={10} type={type} />
                        </div>
                      )}
                      {minimal && actionsAlignment !== "right" && (
                        <div className="flex flex-shrink-0 items-center gap-x-2">
                          <Actions actions={actions} dropdownActions={dropdownActions} />
                          <ToggleVersions
                            standalone
                            showVersions={showVersions}
                            onToggleVersions={onToggleVersions}
                            versionTotal={versionInfo?.total}
                          />
                        </div>
                      )}
                      {actionsAlignment === "right" && actions.length > 0 && (
                        <div className="flex flex-shrink-0 items-center">
                          <Actions actions={actions} dropdownActions={[]} />
                        </div>
                      )}
                    </div>
                    {minimal && actionsAlignment === "right" && rightActions.length > 0 && (
                      <div className="flex flex-shrink-0 items-center gap-x-2">
                        <Actions actions={rightActions} dropdownActions={dropdownActions} />
                        <ToggleVersions
                          standalone
                          showVersions={showVersions}
                          onToggleVersions={onToggleVersions}
                          versionTotal={versionInfo?.total}
                        />
                      </div>
                    )}
                    {!minimal && actionsAlignment === "right" && rightActions.length > 0 && (
                      <div className="flex flex-shrink-0 items-center gap-x-2">
                        <Actions actions={rightActions} dropdownActions={dropdownActions} />
                        <ToggleVersions
                          standalone
                          showVersions={showVersions}
                          onToggleVersions={onToggleVersions}
                          versionTotal={versionInfo?.total}
                        />
                      </div>
                    )}
                  </div>
                </div>
                {(showStar || renderRightContent) && (
                  <div className="ml-auto flex min-w-0 max-w-[70%] flex-shrink-0 items-center justify-end gap-1 overflow-hidden">
                    {showStar && type && <EntityStarButton entityType={type} entityId={id} />}
                    {renderRightContent}
                  </div>
                )}
              </div>

              {!minimal && actionsAlignment !== "right" && (
                <div className="flex min-w-0 items-center justify-between gap-x-3 gap-y-2">
                  <MetaInfo
                    createdAt={createdAt}
                    additionalInfo={additionalInfo}
                    id={id}
                    versionInfo={versionInfo}
                    format={format}
                    showVersions={showVersions}
                    onToggleVersions={onToggleVersions}
                    isLoading={isLoading}
                  />
                  <div className="min-w-0 max-w-full flex-shrink-0 overflow-x-auto sm:ml-auto">
                    <div className="flex-shrink-0">
                      <Actions actions={actions} dropdownActions={dropdownActions} />
                    </div>
                  </div>
                </div>
              )}
              {!minimal && actionsAlignment === "right" && (
                <div className="flex flex-col gap-y-2">
                  <MetaInfo
                    createdAt={createdAt}
                    additionalInfo={additionalInfo}
                    id={id}
                    versionInfo={versionInfo}
                    format={format}
                    showVersions={showVersions}
                    onToggleVersions={onToggleVersions}
                    isLoading={isLoading}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="flex min-w-0 items-center justify-between gap-x-3 gap-y-2 pb-3">
              <div className="flex min-w-0 flex-1 items-start gap-x-2 overflow-hidden">
                {type && (
                  <div
                    className={`hidden h-10 w-10 flex-shrink-0 items-center justify-center rounded-full sm:flex ${getItemColor(
                      type,
                    )}`}
                  >
                    <EntityIcon type={type} size="md" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <Title
                    key={`${id}-${title}`}
                    title={title}
                    onTitleEdit={onTitleEdit}
                    type={type}
                  />
                  {shouldRenderDescription && (
                    <Description
                      description={description}
                      onDescriptionEdit={onDescriptionEdit}
                      type={type}
                    />
                  )}
                </div>
              </div>
              <div className="flex min-w-0 max-w-full flex-shrink-0 items-center gap-1 overflow-x-auto sm:ml-auto">
                {showStar && type && <EntityStarButton entityType={type} entityId={id} />}
                <Actions actions={actions} dropdownActions={dropdownActions} />
              </div>
            </div>
          )}
        </div>
      </div>
      {tabsProps && (
        <div className="min-h-0 flex-1 overflow-auto">
          <TabNavigation {...tabsProps} />
        </div>
      )}
    </div>
  );
};
